import _regeneratorRuntime from "@babel/runtime/helpers/regeneratorRuntime";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _get from "lodash/get";
import appConfig from '@nerdwallet/app-config';
import { getCookiesFromString } from '@nerdwallet/api-sdk-core';
import createNerdwalletAnalytics, { getBaseEventOnCaptureFields, getBaseEventOnTransportFields, getDeviceIdWeb as getDeviceId } from './createNerdwalletAnalytics';
import getNetworkConnectionType from './getNetworkConnectionType';
import deepMergeInto from './deepMergeInto';
import attachBrowserListeners from './browserEvents';
import { GLOBAL_SHARED_STATE_KEY, BROWSER_EVENTS_ATTACHED_KEY } from './constants';

// In our application pages, there are usually TWO different react applications running:
//   1) global-markup
//   2) the application itself
// In that case, there are two different instances of `analytics` in play.
// The `analytics` state (accessed by analytics.getState and analytics.setState)
// needs to be shared between those instances in order to ensure that events fired from global-markup
// and from the application itself use the same state values
// eslint-disable-next-line no-multi-assign
var state = global[GLOBAL_SHARED_STATE_KEY] = global[GLOBAL_SHARED_STATE_KEY] || {};

// create an analytics instance configured for usage in browser
var analytics = createNerdwalletAnalytics({
  platform: 'web',
  appConfig: appConfig,
  state: state
});

/**
 * Attaches global listeners that will automatically fire events when the
 * user navigates between pages or shows/hides a NerdWallet tab. Defaults
 * to firing the events on the default browser analytics instance, but
 * an alternative instance can be provided.
 */
function initBrowserEvents() {
  var analyticsInstance = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : analytics;
  if (process.browser === true) {
    if (analyticsInstance.getState(BROWSER_EVENTS_ATTACHED_KEY) === true) {
      // Warn if another instance of analytics already attached global event
      // listeners.
      /* eslint-disable-next-line no-console */
      console.error('[@nerdwallet/analytics] initBrowserEvents was called more than once.');
      return;
    }
    setTimeout(function () {
      analyticsInstance.track('page_liveness_asserted', {
        navigation_id: analyticsInstance.getState('navigation_id')
      });
    }, 15 * 1000);

    // Load this eagerly so we can throw as soon as possible, if needed
    var YAK_BASE_URL = _get(appConfig, 'API.BASE_URLS.YAK');
    if (!YAK_BASE_URL) {
      throw new Error('API.BASE_URLS.YAK app-configs must be defined');
    }
    var lastLocation = null;

    // automatic tracking of `user_navigated` when history state changes
    var onLocationChanged = function onLocationChanged() {
      // Don't track navigation events if the page is embedded within an iframe
      // via srcdoc.
      if (window.location.href === 'about:srcdoc') return;

      // Don't fire another event if the location didn't actually change.
      // https://nerdwallet.atlassian.net/browse/FEI-1442
      if (window.location.href === lastLocation) return;
      lastLocation = window.location.href;
      analyticsInstance.track('user_navigated');
    };

    // automatic tracking of NerdWallet tabs becoming visible/hidden
    var onVisibilityChanged = function onVisibilityChanged() {
      return analyticsInstance.track('web_visibility_changed');
    };

    // automatic tracking of `user_navigated` on page startup
    var onDOMContentLoaded = function onDOMContentLoaded() {
      return onLocationChanged();
    };

    // automatic tracking of `web_unload_beaconed` on page unload
    var onUnload = /*#__PURE__*/function () {
      var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee() {
        var UNLOAD_EVENT_TYPE, payload, body;
        return _regeneratorRuntime().wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                if (navigator.sendBeacon) {
                  _context.next = 2;
                  break;
                }
                return _context.abrupt("return");
              case 2:
                UNLOAD_EVENT_TYPE = 'web_unload_beaconed';
                payload = {
                  navigation_id: analyticsInstance.getState('navigation_id')
                };
                deepMergeInto(payload, getBaseEventOnCaptureFields(payload, {
                  analytics: analytics,
                  appConfig: appConfig,
                  cookies: getCookiesFromString(window.document.cookie),
                  // Ideally these would come from the config that this instance of
                  // analytics was configured with; oh well.
                  getNetworkConnectionType: getNetworkConnectionType,
                  getDeviceId: getDeviceId
                }));
                // Note: in the normal event pipeline, values from `payload` can override
                // value from `onCapture`. This is a simple case though, so we skip that
                // behavior here.
                deepMergeInto(payload, getBaseEventOnTransportFields(payload));
                body = JSON.stringify({
                  envelope: {
                    exchangeId: 'ANALYTIC_PIPELINE_MOUTH',
                    type: UNLOAD_EVENT_TYPE
                  },
                  payload: payload
                });
                navigator.sendBeacon("".concat(YAK_BASE_URL, "/async/v1/publish"), body);
              case 8:
              case "end":
                return _context.stop();
            }
          }
        }, _callee);
      }));
      return function onUnload() {
        return _ref.apply(this, arguments);
      };
    }();
    attachBrowserListeners({
      onLocationChanged: onLocationChanged,
      onVisibilityChanged: onVisibilityChanged,
      onDOMContentLoaded: onDOMContentLoaded,
      onUnload: onUnload
    });
    analyticsInstance.setState(BROWSER_EVENTS_ATTACHED_KEY, true);
  }
}
export default analytics;
export { initBrowserEvents };